import React, { useState, useEffect, useCallback } from 'react';
import { userGroupsApi, docManagementApi, documentsApi, dataRoomsApi } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  Users, Shield, Eye, Download, Printer, Pencil, Plus, Trash,
  Clock, File, ChartBar, Lock, Stamp
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import logger from '../lib/logger';

const GROUP_TYPES = [
  { id: 'internal_team', label: 'Internal Team', color: 'bg-blue-500' },
  { id: 'external_advisors', label: 'External Advisors', color: 'bg-purple-500' },
  { id: 'bidders', label: 'Bidders', color: 'bg-amber-500' },
  { id: 'investors', label: 'Investors', color: 'bg-emerald-500' },
  { id: 'legal', label: 'Legal', color: 'bg-red-500' },
  { id: 'management', label: 'Management', color: 'bg-cyan-500' },
  { id: 'custom', label: 'Custom', color: 'bg-zinc-500' },
];

export default function DataManagementPage() {
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isGroupOpen, setIsGroupOpen] = useState(false);
  const [isPermOpen, setIsPermOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [docPerms, setDocPerms] = useState({ watermark_enabled: false, restrict_download: false, permissions: [] });
  const [groupForm, setGroupForm] = useState({ name: '', group_type: 'internal_team', description: '', default_permissions: { view: true, download: false, print: false, edit: false }, access_expiry: '' });

  const fetchGroups = useCallback(async () => {
    try {
      const res = await userGroupsApi.getAll();
      setGroups(res.data);
    } catch (error) { logger.error(error); }
  }, []);

  const fetchRooms = useCallback(async () => {
    try {
      const res = await dataRoomsApi.getAll({ limit: 100 });
      setRooms(res.data);
    } catch (error) { logger.error(error); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchGroups(); fetchRooms(); }, [fetchGroups, fetchRooms]);

  const loadRoomData = async (room) => {
    setSelectedRoom(room);
    try {
      const [docsRes, analyticsRes] = await Promise.all([
        documentsApi.getAll({ data_room_id: room.id }),
        docManagementApi.getRoomAnalytics(room.id).catch(() => ({ data: null }))
      ]);
      setDocuments(docsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) { toast.error('Failed to load room data'); }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();
    try {
      await userGroupsApi.create(groupForm);
      toast.success('Group created');
      setIsGroupOpen(false);
      setGroupForm({ name: '', group_type: 'internal_team', description: '', default_permissions: { view: true, download: false, print: false, edit: false }, access_expiry: '' });
      fetchGroups();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteGroup = async (id) => {
    if (!window.confirm('Delete this group?')) return;
    try { await userGroupsApi.delete(id); toast.success('Deleted'); fetchGroups(); }
    catch { toast.error('Failed'); }
  };

  const openPermissions = async (doc) => {
    setSelectedDoc(doc);
    try {
      const res = await docManagementApi.getPermissions(doc.id);
      setDocPerms(res.data);
      setIsPermOpen(true);
    } catch { setDocPerms({ watermark_enabled: false, restrict_download: false, permissions: [] }); setIsPermOpen(true); }
  };

  const savePermissions = async () => {
    try {
      await docManagementApi.setPermissions(selectedDoc.id, docPerms);
      toast.success('Permissions saved');
      setIsPermOpen(false);
    } catch (error) { toast.error('Failed to save'); }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';

  return (
    <div className="space-y-6" data-testid="data-management-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Data Management</h1>
          <p className="text-muted-foreground text-sm mt-1">Ansarada-style access control, document tracking & analytics</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setIsGroupOpen(true)} className="rounded-lg" data-testid="new-group-btn">
            <Plus size={18} className="mr-2" /> New User Group
          </Button>
        )}
      </div>

      <Tabs defaultValue="groups">
        <TabsList>
          <TabsTrigger value="groups"><Users size={16} className="mr-1" /> User Groups</TabsTrigger>
          <TabsTrigger value="rooms"><Lock size={16} className="mr-1" /> Room Access</TabsTrigger>
          <TabsTrigger value="analytics"><ChartBar size={16} className="mr-1" /> Activity Analytics</TabsTrigger>
        </TabsList>

        {/* User Groups Tab */}
        <TabsContent value="groups" className="space-y-4">
          {groups.length === 0 ? (
            <div className="text-center p-12 border border-dashed border-border rounded-xl">
              <Users size={40} className="mx-auto text-muted-foreground mb-3 opacity-40" />
              <p className="text-muted-foreground">No user groups created</p>
              <p className="text-xs text-muted-foreground mt-1">Create groups to manage access levels</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {groups.map(group => {
                const typeConfig = GROUP_TYPES.find(t => t.id === group.group_type) || GROUP_TYPES[6];
                return (
                  <div key={group.id} className="bg-card border border-border rounded-xl p-5" data-testid={`group-${group.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`h-10 w-10 rounded-lg ${typeConfig.color} flex items-center justify-center`}>
                          <Users size={20} className="text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold">{group.name}</h3>
                          <Badge variant="outline" className="text-xs mt-0.5">{typeConfig.label}</Badge>
                        </div>
                      </div>
                      {isAdmin && (
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDeleteGroup(group.id)}>
                          <Trash size={14} />
                        </Button>
                      )}
                    </div>
                    {group.description && <p className="text-sm text-muted-foreground mb-3">{group.description}</p>}
                    <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                      <span>{group.member_count} members</span>
                      {group.access_expiry && <span className="flex items-center gap-1"><Clock size={12} /> Expires {formatDate(group.access_expiry)}</span>}
                    </div>
                    <div className="flex gap-3 pt-3 border-t border-border">
                      <PermIcon enabled={group.default_permissions?.view} icon={Eye} label="View" />
                      <PermIcon enabled={group.default_permissions?.download} icon={Download} label="Download" />
                      <PermIcon enabled={group.default_permissions?.print} icon={Printer} label="Print" />
                      <PermIcon enabled={group.default_permissions?.edit} icon={Pencil} label="Edit" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Room Access Tab */}
        <TabsContent value="rooms" className="space-y-4">
          <div className="flex gap-6">
            {/* Room List */}
            <div className="w-72 flex-shrink-0 space-y-2">
              <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground px-2">Data Rooms</p>
              {rooms.map(room => (
                <div
                  key={room.id}
                  className={`p-3 rounded-lg cursor-pointer transition-all ${selectedRoom?.id === room.id ? 'bg-primary/10 border border-primary/30' : 'bg-card border border-border hover:bg-muted/50'}`}
                  onClick={() => loadRoomData(room)}
                >
                  <p className="font-medium text-sm">{room.name}</p>
                  <p className="text-xs text-muted-foreground">{room.document_count || 0} documents</p>
                </div>
              ))}
              {rooms.length === 0 && <p className="text-sm text-muted-foreground p-4">No data rooms</p>}
            </div>

            {/* Documents with permissions */}
            <div className="flex-1">
              {selectedRoom ? (
                <div className="space-y-3">
                  <h3 className="font-heading text-lg font-semibold">{selectedRoom.name}</h3>
                  {documents.length === 0 ? (
                    <p className="text-muted-foreground p-8 text-center">No documents in this room</p>
                  ) : (
                    <div className="bg-card border border-border rounded-xl overflow-hidden">
                      <table className="w-full">
                        <thead><tr className="border-b border-border bg-muted/50">
                          <th className="text-left p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Document</th>
                          <th className="text-center p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Watermark</th>
                          <th className="text-center p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Restrict DL</th>
                          <th className="text-center p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Version</th>
                          <th className="text-right p-3 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Actions</th>
                        </tr></thead>
                        <tbody>
                          {documents.map(doc => (
                            <tr key={doc.id} className="border-b border-border hover:bg-muted/30">
                              <td className="p-3">
                                <div className="flex items-center gap-2">
                                  <File size={16} className="text-primary" />
                                  <div>
                                    <p className="font-medium text-sm">{doc.name}</p>
                                    <p className="text-xs text-muted-foreground">{doc.original_filename}</p>
                                  </div>
                                </div>
                              </td>
                              <td className="p-3 text-center">
                                {doc.watermark_enabled ? <Stamp size={16} className="text-amber-500 mx-auto" /> : <span className="text-muted-foreground text-xs">-</span>}
                              </td>
                              <td className="p-3 text-center">
                                {doc.restrict_download ? <Lock size={16} className="text-red-500 mx-auto" /> : <span className="text-muted-foreground text-xs">-</span>}
                              </td>
                              <td className="p-3 text-center text-sm">v{doc.current_version || 1}</td>
                              <td className="p-3 text-right">
                                {isAdmin && (
                                  <Button variant="outline" size="sm" onClick={() => openPermissions(doc)} data-testid={`perms-${doc.id}`}>
                                    <Shield size={14} className="mr-1" /> Permissions
                                  </Button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center justify-center h-48 text-muted-foreground">
                  <p>Select a data room to manage access</p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Activity Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          {!selectedRoom ? (
            <div className="text-center p-12 border border-dashed border-border rounded-xl text-muted-foreground">
              <ChartBar size={40} className="mx-auto mb-3 opacity-40" />
              <p>Select a data room from the "Room Access" tab to view analytics</p>
            </div>
          ) : !analytics ? (
            <div className="text-center p-8 text-muted-foreground">No activity data for this room</div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <StatCard label="Total Views" value={analytics.total_views} />
                <StatCard label="Downloads" value={analytics.total_downloads} />
                <StatCard label="Unique Viewers" value={analytics.unique_viewers} />
                <StatCard label="Documents" value={documents.length} />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Viewers */}
                <div className="bg-card border border-border p-6 rounded-xl">
                  <h3 className="font-heading font-semibold mb-4">Most Active Viewers</h3>
                  <div className="space-y-2">
                    {analytics.top_viewers?.length > 0 ? analytics.top_viewers.map((v, i) => (
                      <div key={i} className="flex items-center justify-between p-2 bg-muted/50 rounded-lg">
                        <span className="text-sm font-medium">{v.user_name}</span>
                        <div className="text-right">
                          <span className="text-sm">{v.views} views</span>
                          {v.time_spent > 0 && <p className="text-xs text-muted-foreground">{Math.round(v.time_spent / 60)}min</p>}
                        </div>
                      </div>
                    )) : <p className="text-muted-foreground text-sm">No viewer data</p>}
                  </div>
                </div>

                {/* Most Viewed Documents */}
                <div className="bg-card border border-border p-6 rounded-xl">
                  <h3 className="font-heading font-semibold mb-4">Most Viewed Documents</h3>
                  <div className="h-64">
                    {analytics.most_viewed_docs?.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={analytics.most_viewed_docs} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis type="number" stroke="hsl(var(--muted-foreground))" />
                          <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                          <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))' }} />
                          <Bar dataKey="views" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : <p className="text-muted-foreground text-center h-full flex items-center justify-center">No data</p>}
                  </div>
                </div>
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>

      {/* Create User Group Dialog */}
      <Dialog open={isGroupOpen} onOpenChange={setIsGroupOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>New User Group</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateGroup} className="space-y-4">
            <div className="space-y-2">
              <Label>Group Name *</Label>
              <Input value={groupForm.name} onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })} required data-testid="group-name" />
            </div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={groupForm.group_type} onValueChange={(v) => setGroupForm({ ...groupForm, group_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{GROUP_TYPES.map(t => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={groupForm.description} onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })} rows={2} />
            </div>
            <div className="space-y-2">
              <Label>Access Expiry (optional)</Label>
              <Input type="date" value={groupForm.access_expiry} onChange={(e) => setGroupForm({ ...groupForm, access_expiry: e.target.value })} />
            </div>
            <div>
              <Label className="mb-2 block">Default Permissions</Label>
              <div className="grid grid-cols-2 gap-3">
                {[{ key: 'view', label: 'View' }, { key: 'download', label: 'Download' }, { key: 'print', label: 'Print' }, { key: 'edit', label: 'Edit' }].map(p => (
                  <div key={p.key} className="flex items-center gap-2">
                    <Checkbox checked={groupForm.default_permissions[p.key]} onCheckedChange={(v) => setGroupForm({ ...groupForm, default_permissions: { ...groupForm.default_permissions, [p.key]: v } })} />
                    <label className="text-sm">{p.label}</label>
                  </div>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsGroupOpen(false)}>Cancel</Button>
              <Button type="submit" data-testid="group-submit">Create Group</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Document Permissions Dialog */}
      <Dialog open={isPermOpen} onOpenChange={setIsPermOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Document Permissions</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {selectedDoc && (
              <div className="p-3 bg-muted rounded-lg">
                <p className="font-medium text-sm">{selectedDoc.name}</p>
                <p className="text-xs text-muted-foreground">{selectedDoc.original_filename}</p>
              </div>
            )}
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div className="flex items-center gap-2">
                  <Stamp size={18} className="text-amber-500" />
                  <div>
                    <p className="text-sm font-medium">Watermark</p>
                    <p className="text-xs text-muted-foreground">Add watermark with viewer's name</p>
                  </div>
                </div>
                <Switch checked={docPerms.watermark_enabled} onCheckedChange={(v) => setDocPerms({ ...docPerms, watermark_enabled: v })} data-testid="watermark-toggle" />
              </div>
              <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div className="flex items-center gap-2">
                  <Lock size={18} className="text-red-500" />
                  <div>
                    <p className="text-sm font-medium">Restrict Download</p>
                    <p className="text-xs text-muted-foreground">Prevent file downloading</p>
                  </div>
                </div>
                <Switch checked={docPerms.restrict_download} onCheckedChange={(v) => setDocPerms({ ...docPerms, restrict_download: v })} data-testid="restrict-dl-toggle" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPermOpen(false)}>Cancel</Button>
            <Button onClick={savePermissions} data-testid="save-perms-btn">Save Permissions</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PermIcon({ enabled, icon: Icon, label }) {
  return (
    <div className={`flex items-center gap-1 text-xs ${enabled ? 'text-foreground' : 'text-muted-foreground/40'}`}>
      <Icon size={14} />
      <span>{label}</span>
    </div>
  );
}

function StatCard({ label, value }) {
  return (
    <div className="bg-card border border-border p-4 rounded-xl">
      <p className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}
